/**
 * RealtimeClient — template-side WebSocket client for Entity.subscribe().
 *
 * Responsibilities
 *   - Lazy-connect to /ws/entities once some entity has a subscriber
 *   - Fetch a one-time ticket via POST /workflows/:id/auth/ws/ticket
 *   - Track lastSeq per table (persisted) so reconnects replay missed events
 *   - Dedup events by seq so replay + live overlap is a non-issue
 *   - Reconnect with exponential backoff while any subscriber remains
 *   - Re-send subscribe on reconnect with the persisted lastSeq
 *   - Close the WS when the last subscriber unsubscribes
 *
 * Dependency injection
 *   Every side-effect (window, localStorage, WebSocket, fetch) is reached
 *   through an instance method so tests can override a fresh instance with
 *   in-memory fakes and verify protocol behavior without real network.
 */

const LS_SEQ_PREFIX = "realtime-seq:";
const MIN_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 15000;

export class RealtimeClient {
    constructor() {
        this.ws = null;
        this.connecting = null;
        // Map<tableName, Set<{ filter, onEvent }>>
        this.subs = new Map();
        // Map<tableName, string> — high-water-mark for reconnect (sinceSeq).
        this.lastSeq = new Map();
        // Map<tableName, Set<seq>> — recent delivered seqs for dedup.
        // Handles the live-vs-replay ordering gap without losing replayed
        // events that arrive after a live event that advanced the HWM.
        this._seenSeqs = new Map();
        this.backoff = MIN_BACKOFF_MS;
        this._reconnectTimer = null;
        this._shuttingDown = false;
    }

    /**
     * Subscribe to entity events for a given table.
     * @param {string} tableName
     * @param {object|null} filter  Equality-only filter (evaluated client-side
     *                              per-callback; also forwarded to the server
     *                              if ALL callbacks share the same filter so
     *                              we save bandwidth on the common case).
     * @param {(event: object) => void} onEvent
     * @returns {() => void} unsubscribe
     */
    subscribe(tableName, filter, onEvent) {
        if (typeof filter === "function") {
            onEvent = filter;
            filter = null;
        }
        if (typeof onEvent !== "function") {
            throw new Error("subscribe() requires a callback function");
        }

        const entry = {
            filter: filter && typeof filter === "object" ? filter : null,
            onEvent,
        };
        if (!this.subs.has(tableName)) this.subs.set(tableName, new Set());
        this.subs.get(tableName).add(entry);

        // Kick off a connect (lazy — only the first subscriber triggers it).
        this._ensureOpen()
            .then(() => this._resyncServerSubscription(tableName))
            .catch(() => {
                // Reconnect loop retries — nothing to do here.
            });

        return () => this._unsubscribe(tableName, entry);
    }

    /**
     * Recompute the server-side filter for a table and send a (re)subscribe
     * if it changed. The server filter is the common filter across all
     * local callbacks; if callbacks disagree, we send null (receive all)
     * and filter per-callback in `_onMessage`.
     */
    _resyncServerSubscription(tableName) {
        const entries = this.subs.get(tableName);
        if (!entries || entries.size === 0) return;
        const serverFilter = this._commonFilter(entries);
        this._sendSubscribe(tableName, serverFilter);
    }

    _commonFilter(entries) {
        const arr = [...entries];
        if (arr.length === 0) return null;
        const firstStr = JSON.stringify(arr[0].filter ?? null);
        for (let i = 1; i < arr.length; i++) {
            if (JSON.stringify(arr[i].filter ?? null) !== firstStr) return null;
        }
        return arr[0].filter ?? null;
    }

    _unsubscribe(tableName, entry) {
        const set = this.subs.get(tableName);
        if (!set) return;
        set.delete(entry);
        if (set.size === 0) {
            this.subs.delete(tableName);
            this._sendMessage({ type: "unsubscribe", tableName });
        } else {
            // Still other subscribers on this table; re-sync the server
            // filter in case the removed callback was the one holding
            // the common filter together.
            this._resyncServerSubscription(tableName);
        }
        // When the last subscriber leaves, tear the connection down so we
        // stop holding an open socket on idle dashboards.
        if (this.subs.size === 0) {
            this._shuttingDown = true;
            this._clearReconnect();
            try {
                this.ws && this.ws.close && this.ws.close();
            } catch {
                // nothing
            }
            this.ws = null;
        }
    }

    async _ensureOpen() {
        if (this.ws && this._readyState(this.ws) === 1) return;
        if (this.connecting) return this.connecting;
        this._shuttingDown = false;
        this.connecting = this._connect().finally(() => {
            this.connecting = null;
        });
        return this.connecting;
    }

    async _connect() {
        const ticket = await this._fetchTicket();
        const wsUrl = this._wsUrl(ticket);

        await new Promise((resolve, reject) => {
            const WS = this._WebSocketCtor();
            const ws = new WS(wsUrl);

            const clean = () => {
                ws.onopen = null;
                ws.onerror = null;
            };

            ws.onopen = () => {
                clean();
                this.ws = ws;
                this.backoff = MIN_BACKOFF_MS;
                ws.onmessage = (ev) => this._onMessage(ev);
                ws.onclose = () => this._onClose();
                this._resubscribeAll();
                resolve();
            };
            ws.onerror = (e) => {
                clean();
                reject(e);
            };
            // onclose may fire BEFORE onopen if handshake fails.
            ws.onclose = () => {
                if (!this.ws) reject(new Error("WebSocket closed before open"));
            };
        });
    }

    _onClose() {
        this.ws = null;
        if (this._shuttingDown || this.subs.size === 0) return;
        this._scheduleReconnect();
    }

    _scheduleReconnect() {
        if (this._reconnectTimer) return;
        const base = this.backoff;
        this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
        // ±50% jitter — prevents thundering-herd reconnect after deploys
        // or upstream network blips when many clients were all synced.
        const jitter = this._random();
        const delay = Math.max(1, Math.floor(base * (0.5 + jitter)));
        this._reconnectTimer = this._setTimeout(() => {
            this._reconnectTimer = null;
            this._ensureOpen().catch(() => {
                // _onClose will schedule again
            });
        }, delay);
    }

    _random() {
        return Math.random();
    }

    _clearReconnect() {
        if (this._reconnectTimer) {
            this._clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    }

    _resubscribeAll() {
        for (const tableName of this.subs.keys()) {
            // Use the common filter across all current subscribers.
            this._resyncServerSubscription(tableName);
        }
    }

    _sendSubscribe(tableName, filter) {
        const sinceSeq = this._loadLastSeq(tableName);
        this._sendMessage({
            type: "subscribe",
            tableName,
            filter: filter ?? null,
            ...(sinceSeq ? { sinceSeq } : {}),
        });
    }

    _sendMessage(msg) {
        if (!this.ws || this._readyState(this.ws) !== 1) return;
        try {
            this.ws.send(JSON.stringify(msg));
        } catch {
            // socket died between check and send — reconnect loop recovers
        }
    }

    _onMessage(ev) {
        let msg;
        try {
            const raw =
                typeof ev.data === "string" ? ev.data : ev.data.toString();
            msg = JSON.parse(raw);
        } catch {
            return;
        }

        if (msg.type === "replay_overflow") {
            // Stream window exhausted — clear lastSeq so next reconnect
            // gets a fresh slate. App-level code should refetch via REST.
            if (msg.tableName) {
                this._clearLastSeq(msg.tableName);
                this._seenSeqs.delete(msg.tableName);
            }
            return;
        }

        if (msg.type !== "entity_event") return;

        // Dedup by a bounded set of recently-observed seqs per table.
        // Using a "greater than lastSeq" rule alone would incorrectly
        // drop replayed events when a live event has already advanced
        // lastSeq (e.g., replay lands after live due to scheduling).
        if (msg.seq) {
            let seen = this._seenSeqs.get(msg.tableName);
            if (!seen) {
                seen = new Set();
                this._seenSeqs.set(msg.tableName, seen);
            }
            if (seen.has(msg.seq)) return; // already delivered
            seen.add(msg.seq);
            // Bound the set so it doesn't grow forever.
            if (seen.size > 1000) {
                const first = seen.values().next().value;
                seen.delete(first);
            }
            // Still track lastSeq as the high-water-mark for reconnect.
            const prev = this.lastSeq.get(msg.tableName) || null;
            if (!prev || this._seqGreater(msg.seq, prev)) {
                this.lastSeq.set(msg.tableName, msg.seq);
                this._persistLastSeq(msg.tableName, msg.seq);
            }
        }

        const entries = this.subs.get(msg.tableName);
        if (!entries) return;
        // Per-callback filter isolation: each subscriber's filter is
        // evaluated here, regardless of what the server-side union
        // filter let through.
        for (const { filter, onEvent } of entries) {
            if (!this._matchesClientFilter(msg.record || msg.before, filter)) {
                continue;
            }
            try {
                onEvent(msg);
            } catch {
                // User callback threw — swallow so other subscribers still fire.
            }
        }
    }

    _matchesClientFilter(record, filter) {
        if (!filter) return true;
        if (!record) return false;
        for (const [k, v] of Object.entries(filter)) {
            if (record[k] !== v) return false;
        }
        return true;
    }

    // Stream IDs are "<ms>-<seq>" where both parts are decimal integers.
    // Compare as 2-part numbers (string compare would be wrong once ms grows).
    _seqGreater(a, b) {
        const [a1, a2] = String(a).split("-").map(Number);
        const [b1, b2] = String(b).split("-").map(Number);
        if (a1 !== b1) return a1 > b1;
        return (a2 || 0) > (b2 || 0);
    }

    // ─── Injection points (overridable on test instances) ─────────────

    _WebSocketCtor() {
        // eslint-disable-next-line no-undef
        return typeof WebSocket !== "undefined" ? WebSocket : globalThis.WebSocket;
    }

    _setTimeout(fn, ms) {
        return setTimeout(fn, ms);
    }

    _clearTimeout(id) {
        return clearTimeout(id);
    }

    _readyState(ws) {
        return ws && typeof ws.readyState === "number" ? ws.readyState : 1;
    }

    _getWorkflowId() {
        if (typeof window !== "undefined") {
            return window.config?.workflowId ?? window.__CONFIG__?.workflowId ?? null;
        }
        // eslint-disable-next-line no-undef
        if (typeof Deno !== "undefined") return Deno.env.get("WORKFLOW_ID");
        return null;
    }

    _getAuthToken() {
        const wf = this._getWorkflowId();
        if (!wf) return null;
        if (typeof localStorage !== "undefined") {
            return localStorage.getItem(`workflow-auth-${wf}`);
        }
        return null;
    }

    _getApiUrl() {
        if (typeof window !== "undefined") {
            // Explicit overrides win — they're how tests and custom embeds
            // redirect the realtime channel without touching window.location.
            const override =
                window.__ENV__?.API_BASE_URL ||
                window.config?.apiUrl ||
                window.__CONFIG__?.apiUrl;
            if (override) return override;
            // Mirror dbAdapter's hostname-based resolution so live data and
            // realtime land on the SAME server. Otherwise an app on
            // localhost would write to :3333 but subscribe against prod
            // (ticket endpoint returns 404 on the wrong server).
            const hostname = window?.location?.hostname ?? "";
            const isStaging =
                hostname.includes("staging-apps.agentui.ai") ||
                hostname.includes("staging-api.agentui.ai") ||
                hostname === "staging.agentui.ai";
            if (hostname === "localhost" || hostname.includes("agentui.test"))
                return "http://localhost:3333";
            if (isStaging) return "https://staging-api.agentui.ai";
            return "https://api.agentui.ai";
        }
        // eslint-disable-next-line no-undef
        if (typeof Deno !== "undefined") {
            return Deno.env.get("API_BASE_URL") || "https://api.agentui.ai";
        }
        return "https://api.agentui.ai";
    }

    _wsUrl(ticket) {
        const api = this._getApiUrl();
        const base = api.replace(/^http/, "ws");
        return `${base}/ws/entities?ticket=${encodeURIComponent(ticket)}`;
    }

    async _fetchTicket() {
        const workflowId = this._getWorkflowId();
        if (!workflowId) {
            throw new Error("RealtimeClient: workflowId not configured");
        }
        const api = this._getApiUrl();
        const token = this._getAuthToken();
        // Pass the environment so the server can resolve the correct
        // env-specific database (prod vs staging may share table names).
        const environmentName = this._getEnvironmentName();
        const body = environmentName ? { environmentName } : {};
        const res = await this._httpPost(
            `${api}/workflows/${workflowId}/auth/ws/ticket`,
            body,
            token ? { Authorization: `Bearer ${token}` } : {}
        );
        const ticket =
            res?.data?.data?.ticket ??
            res?.data?.ticket ??
            null;
        if (!ticket) throw new Error("RealtimeClient: no ticket returned");
        return ticket;
    }

    _getEnvironmentName() {
        // ComponentWriter injects the env as `environment` (not
        // `environmentName`) into window.__CONFIG__ / window.config.
        // Missing or "production" → server defaults to production.
        if (typeof window !== "undefined") {
            return (
                window.config?.environment ||
                window.__CONFIG__?.environment ||
                window.config?.environmentName ||
                window.__CONFIG__?.environmentName ||
                null
            );
        }
        // eslint-disable-next-line no-undef
        if (typeof Deno !== "undefined") {
            return (
                Deno.env.get("ENVIRONMENT_NAME") ||
                Deno.env.get("ENVIRONMENT") ||
                null
            );
        }
        return null;
    }

    async _httpPost(url, body, headers = {}) {
        // eslint-disable-next-line no-undef
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...headers },
            body: JSON.stringify(body ?? {}),
        });
        const data = await res.json().catch(() => null);
        return { status: res.status, data };
    }

    _loadLastSeq(tableName) {
        if (this.lastSeq.has(tableName)) return this.lastSeq.get(tableName);
        if (typeof localStorage === "undefined") return null;
        const v = localStorage.getItem(`${LS_SEQ_PREFIX}${tableName}`);
        if (v) this.lastSeq.set(tableName, v);
        return v || null;
    }

    _persistLastSeq(tableName, seq) {
        if (typeof localStorage === "undefined") return;
        try {
            localStorage.setItem(`${LS_SEQ_PREFIX}${tableName}`, seq);
        } catch {
            // quota / private mode — accept that replay won't help
        }
    }

    _clearLastSeq(tableName) {
        this.lastSeq.delete(tableName);
        if (typeof localStorage === "undefined") return;
        try {
            localStorage.removeItem(`${LS_SEQ_PREFIX}${tableName}`);
        } catch {
            // nothing
        }
    }
}

const singleton = new RealtimeClient();
export default singleton;
